import { Badge } from "../../../../core/Badge.js";
import { Skeleton } from "../../../../core/Skeleton.js";
import { Text } from "../../../../core/Text.js";
import { BoxHeading } from "../../../../domain/BoxHeading.js";
import { useBoxHeroHeading } from "../../../../domain/boxHeroHeading.js";
import { cn } from "../../../../foundations/cn.js";
import { ActionButton } from "../../../shared/ActionButton.js";
import { LoadError } from "../../../shared/LoadError.js";
import type {
  BoxPageHeadingContent,
  BoxPageHeading as BoxPageHeadingFields,
} from "../../boxPageContent.js";

export type BoxPageHeadingProps = BoxPageHeadingContent & {
  /** Left-aligned in a column, or centered under a stage. */
  align?: "start" | "center";
  className?: string;
};

/**
 * The box's name (the page's h1) and price, who built it and which drop table version it opens
 * against, with what else to do with it. A stand-in while the box loads; why it failed if it did.
 */
export function BoxPageHeading({ align = "start", className, ...heading }: BoxPageHeadingProps) {
  const heroHasHeading = useBoxHeroHeading();
  const center = align === "center";
  if (heading.status === "loading") {
    return (
      <div aria-hidden className={cn("flex flex-col gap-4", center && "items-center", className)}>
        <Skeleton className="h-10 w-64 rounded-md" />
        <Skeleton className="h-6 w-40 rounded-sm" />
      </div>
    );
  }
  if (heading.status === "error") {
    return (
      <div className={className}>
        <LoadError message={heading.message} />
      </div>
    );
  }
  if (heading.inHero && heroHasHeading) return null;
  const { creator, version, actions } = heading;
  const meta =
    creator || version || actions.length > 0 ? (
      <BoxPageHeadingMeta creator={creator} version={version} actions={actions} align={align} />
    ) : undefined;
  return (
    <BoxHeading
      name={heading.name}
      price={heading.price}
      priceNote={heading.priceNote}
      meta={meta}
      align={align}
      className={className}
    />
  );
}

/**
 * Under the price: who built the box, the drop table version it opens against, and what else to
 * do with it. The heading draws it, and so does the opener's idle hero when it takes the heading's
 * place.
 */
export function BoxPageHeadingMeta({
  creator,
  version,
  actions,
  align = "start",
}: Pick<BoxPageHeadingFields, "creator" | "version" | "actions"> & { align?: "start" | "center" }) {
  if (!creator && !version && actions.length === 0) return null;
  return (
    <div
      className={cn("flex flex-wrap items-center gap-3", align === "center" && "justify-center")}
    >
      {creator && (
        <Text variant="body-b3" tone="tertiary">
          {creator.label}{" "}
          <Text as="strong" variant="body-b3" tone="primary" className="font-semibold">
            {creator.name}
          </Text>
        </Text>
      )}
      {version && <Badge className="font-mono normal-case tracking-normal">{version}</Badge>}
      {actions.map((action) => (
        <ActionButton
          key={action.label}
          action={action}
          variant={action.emphasis === "primary" ? "brand" : "secondary"}
          size="sm"
        />
      ))}
    </div>
  );
}
